import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, FileArrowUp, FileText, LockKey,
  Plus, SealCheck, ShieldCheck, Users,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LegalDocumentActions from "@/components/LegalDocumentActions";

const inputClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const fallbackLabel = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    gold: "border-[#C5A059]/40 bg-[#C5A059]/10 text-[#8A692D]",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <span className={`inline-flex border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-slate-600">
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

const DOCUMENT_CARD_TYPES = [
  "nda",
  "ncnd",
  "seller_mandate",
  "buyer_mandate",
  "commission_agreement",
  "fee_protection_agreement",
  "introducer_agreement",
  "co_broker_agreement",
  "opportunity_acknowledgement",
];

function CreateDocumentForm({ meta, parties, initialType, saving, onCancel, onSave, t }) {
  const [form, setForm] = useState({
    title: fallbackLabel(initialType || "nda"),
    document_type: initialType || "nda",
    language: "pt",
    secondary_language: "",
    legal_framework: "portuguese_law_portuguese_courts",
    governing_law: "Portuguese law",
    jurisdiction: "Portugal",
    dispute_mechanism: "negotiation",
    party_ids: [],
    confidentiality_required: true,
    non_circumvention_required: false,
  });

  const set = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const option = (value) =>
    t(`legalDocuments.options.${value}`, fallbackLabel(value));

  const select = (field, values) => (
    <select
      value={form[field] || ""}
      onChange={(event) => set(field, event.target.value)}
      className={inputClass}
    >
      {(values || []).map((value) => (
        <option key={value} value={value}>{option(value)}</option>
      ))}
    </select>
  );

  const submit = (event) => {
    event.preventDefault();
    onSave({
      ...form,
      secondary_language: form.secondary_language || null,
      governing_law: form.governing_law || null,
      jurisdiction: form.jurisdiction || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6">
      <div className="mx-auto w-full max-w-4xl bg-[#F8F9FA] shadow-2xl">
        <div className="bg-[#0B2545] px-6 py-5 text-white">
          <p className="mb-1 text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">
            Panatlantik Legal & Compliance
          </p>
          <h2 className="font-serif text-2xl">{t("legalDocuments.create")}</h2>
        </div>
        <form onSubmit={submit} className="space-y-6 p-5 sm:p-7">
          <section className="border border-slate-200 bg-white p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Field label={t("legalDocuments.fields.title")} required>
                  <Input
                    value={form.title}
                    onChange={(event) => set("title", event.target.value)}
                    className="h-11 rounded-none"
                    required
                  />
                </Field>
              </div>
              <Field label={t("legalDocuments.fields.documentType")}>
                {select("document_type", meta?.document_types)}
              </Field>
              <Field label={t("legalDocuments.fields.legalFramework")}>
                {select("legal_framework", meta?.legal_frameworks)}
              </Field>
              <Field label={t("legalDocuments.fields.language")}>
                <select
                  value={form.language}
                  onChange={(event) => set("language", event.target.value)}
                  className={inputClass}
                >
                  {["en", "pt", "zh", "fr", "es", "de", "it", "ru"].map((code) => (
                    <option key={code} value={code}>
                      {t(`legalDocuments.languages.${code}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("legalDocuments.fields.secondaryLanguage")}>
                <select
                  value={form.secondary_language}
                  onChange={(event) => set("secondary_language", event.target.value)}
                  className={inputClass}
                >
                  <option value="">{t("legalDocuments.none")}</option>
                  {["en", "pt", "zh", "fr", "es", "de", "it", "ru"].map((code) => (
                    <option key={code} value={code}>
                      {t(`legalDocuments.languages.${code}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("legalDocuments.fields.governingLaw")}>
                <Input
                  value={form.governing_law}
                  onChange={(event) => set("governing_law", event.target.value)}
                  className="h-11 rounded-none"
                />
              </Field>
              <Field label={t("legalDocuments.fields.jurisdiction")}>
                <Input
                  value={form.jurisdiction}
                  onChange={(event) => set("jurisdiction", event.target.value)}
                  className="h-11 rounded-none"
                />
              </Field>
            </div>
          </section>

          <section className="border border-slate-200 bg-white p-5">
            <h3 className="mb-4 flex items-center gap-2 font-serif text-lg text-[#0B2545]">
              <Users size={20} className="text-[#C5A059]" />
              {t("legalDocuments.parties")}
            </h3>
            {parties.length ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {parties.map((party) => (
                  <label key={party.id} className="flex cursor-pointer items-start gap-3 border border-slate-200 p-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={form.party_ids.includes(party.id)}
                      onChange={(event) => set(
                        "party_ids",
                        event.target.checked
                          ? [...form.party_ids, party.id]
                          : form.party_ids.filter((id) => id !== party.id)
                      )}
                    />
                    <span>
                      <span className="block text-sm font-medium text-[#0B2545]">
                        {party.legal_name || party.name}
                      </span>
                      <span className="text-xs text-slate-500">{option(party.party_role)}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{t("legalDocuments.noParties")}</p>
            )}
          </section>

          <section className="border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-900">
            {t("legalDocuments.legalNotice")}
          </section>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel} className="rounded-none">
              {t("legalDocuments.cancel")}
            </Button>
            <Button disabled={saving} className="rounded-none bg-[#0B2545] hover:bg-[#12345e]">
              {saving ? t("legalDocuments.saving") : t("legalDocuments.save")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LegalDocuments() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [createType, setCreateType] = useState(null);

  const option = (value) =>
    t(`legalDocuments.options.${value}`, fallbackLabel(value));

  const { data: opportunity } = useQuery({
    queryKey: ["bank-opportunity", opportunityId],
    queryFn: async () => (await api.get(`/bank-opportunities/${opportunityId}`)).data,
  });
  const { data: meta } = useQuery({
    queryKey: ["deal-legal-meta"],
    queryFn: async () => (await api.get("/deal-legal/meta")).data,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["deal-parties", opportunityId],
    queryFn: async () => (await api.get(`/deal-legal/opportunities/${opportunityId}/parties`)).data,
  });
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["legal-documents", opportunityId],
    queryFn: async () => (await api.get(`/deal-legal/opportunities/${opportunityId}/documents`)).data,
  });

  const stats = useMemo(() => ({
    total: documents.length,
    review: documents.filter((item) => item.status === "pending_legal_review").length,
    approved: documents.filter((item) => item.status === "approved").length,
    signed: documents.filter((item) => item.status === "signed").length,
  }), [documents]);

  const createMutation = useMutation({
    mutationFn: async (payload) =>
      (await api.post(`/deal-legal/opportunities/${opportunityId}/documents`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-documents", opportunityId] });
      setCreateType(null);
      toast.success(t("legalDocuments.created"));
    },
    onError: (error) =>
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || t("legalDocuments.actionFailed")),
  });

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-7 sm:px-7 lg:px-10 lg:py-9">
      <button
        type="button"
        onClick={() => navigate("/bank-opportunities")}
        className="mb-5 flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B2545]"
      >
        <ArrowLeft size={17} /> {t("legalDocuments.back")}
      </button>

      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.24em] text-[#A77E35]">
            Panatlantik Legal & Compliance
          </p>
          <h1 className="font-serif text-3xl text-[#0B2545]">{t("legalDocuments.title")}</h1>
          <p className="mt-2 text-sm text-slate-500">
            {opportunity?.title || t("legalDocuments.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={() => navigate("/bank-opportunities/legal-templates")}
              className="h-12 rounded-none border-[#C5A059] px-6 text-[#0B2545]"
            >
              <FileText size={18} className="mr-2" />
              {t("legalTemplates.navigation", "Legal templates")}
            </Button>
          )}
          <Button
            onClick={() => setCreateType("nda")}
            className="h-12 rounded-none bg-[#0B2545] px-6 hover:bg-[#12345e]"
          >
            <Plus size={18} className="mr-2" /> {t("legalDocuments.create")}
          </Button>
        </div>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          [FileText, "total", stats.total],
          [ShieldCheck, "pendingReview", stats.review],
          [SealCheck, "approved", stats.approved],
          [LockKey, "signed", stats.signed],
        ].map(([Icon, key, value]) => (
          <div key={key} className="flex items-center gap-4 border border-slate-200 bg-white p-5">
            <div className="flex h-11 w-11 items-center justify-center bg-[#0B2545]/[0.06] text-[#0B2545]">
              <Icon size={22} />
            </div>
            <div>
              <div className="font-serif text-2xl text-[#0B2545]">{value}</div>
              <div className="text-xs text-slate-500">{t(`legalDocuments.${key}`)}</div>
            </div>
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-slate-500">{t("legalDocuments.loading")}</div>
      ) : (
        <div>
          <div className="mb-4">
            <h2 className="font-serif text-2xl text-[#0B2545]">
              {t("legalDocuments.documentCentre", "Document centre")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t(
                "legalDocuments.documentCentreHelp",
                "Open, generate, upload, download and track each document from its permanent card."
              )}
            </p>
          </div>
          <div className="grid auto-rows-fr items-stretch gap-5 lg:grid-cols-2">
          {DOCUMENT_CARD_TYPES.map((documentType) => {
            const matching = documents
              .filter((item) => item.document_type === documentType)
              .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
            const document = matching[0];
            if (!document) {
              return (
                <article key={documentType} className="flex h-full flex-col border border-dashed border-slate-300 bg-white">
                  <div className="h-1 bg-slate-200" />
                  <div className="flex min-h-[220px] flex-1 flex-col justify-between p-5">
                    <div>
                      <div className="mb-4 flex h-11 w-11 items-center justify-center bg-[#0B2545]/[0.06] text-[#0B2545]">
                        <FileText size={23} />
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#A77E35]">
                        {t("legalDocuments.readySlot", "Ready document slot")}
                      </p>
                      <h3 className="mt-1 font-serif text-xl text-[#0B2545]">
                        {option(documentType)}
                      </h3>
                      <p className="mt-2 text-sm text-slate-500">
                        {t("legalDocuments.notCreatedYet", "Not created for this opportunity yet.")}
                      </p>
                        <div className="mt-6 border-y border-slate-100 py-5">
                          <p className="mb-3 text-[10px] uppercase tracking-[0.18em] text-[#A77E35]">
                            {t("legalDocuments.documentReadiness", "Document readiness")}
                          </p>
                          <div className="grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                            {[
                              t("legalDocuments.ptEnTemplates", "Portuguese and English templates"),
                              t("legalDocuments.pdfDocxGeneration", "PDF and DOCX generation"),
                              t("legalDocuments.signedVersionUpload", "Signed-version upload"),
                              t("legalDocuments.adminControlled", "Administrator-controlled workflow"),
                            ].map((label) => (
                              <div key={label} className="flex items-start gap-2">
                                <CheckCircle size={15} className="mt-0.5 shrink-0 text-[#C5A059]" />
                                <span>{label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setCreateType(documentType)}
                      className="mt-5 h-10 w-full rounded-none bg-[#0B2545] text-xs hover:bg-[#12345e]"
                    >
                      <Plus size={15} className="mr-2" />
                      {t("legalDocuments.createThisDocument", "Create this document")}
                    </Button>
                  </div>
                </article>
              );
            }
            return (
            <article key={documentType} className="flex h-full flex-col border border-slate-200 bg-white">
              <div className="h-1 bg-gradient-to-r from-[#0B2545] via-[#C5A059] to-[#0B2545]" />
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-[#A77E35]">
                      {option(document.document_type)}
                    </p>
                    <h2 className="font-serif text-xl text-[#0B2545]">{document.title}</h2>
                  </div>
                  <Badge tone={document.status === "signed" ? "green" : document.status === "approved" ? "blue" : "gold"}>
                    {option(document.status)}
                  </Badge>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-y border-slate-100 py-4 text-sm">
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-400">
                      {t("legalDocuments.version")}
                    </span>
                    <span className="text-[#0B2545]">v{document.version || 0}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-400">
                      {t("legalDocuments.legalReview")}
                    </span>
                    <span className="text-[#0B2545]">{option(document.legal_review_status)}</span>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge>{option(document.language)}</Badge>
                  {document.confidentiality_required && (
                    <Badge tone="amber">{t("legalDocuments.confidential")}</Badge>
                  )}
                  {document.signed_file_sha256 && (
                    <Badge tone="green"><CheckCircle size={12} className="mr-1" /> SHA-256</Badge>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                  <FileArrowUp size={15} />
                  {document.current_file_id
                    ? t("legalDocuments.fileRecorded")
                    : t("legalDocuments.noFile")}
                </div>
                <LegalDocumentActions
                  document={document}
                  opportunityId={opportunityId}
                />
                {matching.length > 1 && (
                  <p className="mt-3 text-xs text-slate-400">
                    {matching.length - 1} {t("legalDocuments.olderVersions", "older document record(s) retained")}
                  </p>
                )}
              </div>
            </article>
            );
          })}
          </div>
        </div>
      )}

      {createType && (
        <CreateDocumentForm
          key={createType}
          meta={meta}
          parties={parties}
          initialType={createType}
          saving={createMutation.isPending}
          onCancel={() => setCreateType(null)}
          onSave={(payload) => createMutation.mutate(payload)}
          t={t}
        />
      )}
    </div>
  );
}
